import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { PageHeader } from '@/components/ui/PageHeader'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { DataTable } from '@/components/ui/DataTable'
import { useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import { isSuperUser } from '@/util'
import { RestSchemas } from '@/contexts/QueryProvider'
import { Suspense, useCallback, useMemo, useState } from 'react'
import { Navigate } from 'react-router'
import { PlatformBreadcrumbs } from '../PlatformBreadcrumbs'
import { RedisMenu } from '../RedisMenu'
import { PlatformTicketRequestsFilters } from './PlatformTicketRequestsFilters'
import { getPlatformTicketRequestColumns } from './platformTicketRequestsTableColumns'
import { TicketLookupsProvider } from './TicketLookupsContext'
import { PlatformTicketRequestDetailSheet } from './PlatformTicketRequestDetailSheet'
import { buildTicketLookupMaps } from './ticketLookups'

type ExistingRequest = RestSchemas['Messages.Requests.ExistingRequest']

export function PlatformTicketRequestsPage() {
  const [userRole] = useSessionStorage('roles', 1)

  if (!isSuperUser(userRole)) {
    return <Navigate to="/" replace />
  }

  return (
    <>
      <PageHeader className="pr-3" title={<PlatformBreadcrumbs title="Requests" />}>
        <RedisMenu />
      </PageHeader>
      <QueryErrorBoundary errorMessage="Failed to fetch requests">
        <Suspense fallback={<LoadingComponent className="flex-1" />}>
          <PlatformTicketRequestsPageContent />
        </Suspense>
      </QueryErrorBoundary>
    </>
  )
}

function PlatformTicketRequestsPageContent() {
  const { getRequestsApiGetAdminRequests } = useRestClient()
  const { data: response } = getRequestsApiGetAdminRequests.useSuspenseQuery(undefined)
  const requests = response.requests
  const lookups = useMemo(() => buildTicketLookupMaps(response.lookups), [response.lookups])
  const [detailRequest, setDetailRequest] = useState<ExistingRequest>()
  const openDetails = useCallback((request: ExistingRequest) => setDetailRequest(request), [])
  const columns = useMemo(() => getPlatformTicketRequestColumns(openDetails), [openDetails])

  return (
    <TicketLookupsProvider lookups={lookups}>
      {detailRequest && (
        <PlatformTicketRequestDetailSheet
          request={detailRequest}
          open
          onOpenChange={(open) => {
            if (!open) setDetailRequest(undefined)
          }}
          lookups={lookups}
        />
      )}
      <TablePageContainer>
        <DataTable
          title="Access request tickets"
          columns={columns}
          data={requests ?? []}
          filters={(table) => <PlatformTicketRequestsFilters table={table} />}
        />
      </TablePageContainer>
    </TicketLookupsProvider>
  )
}
